import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { extractGarmentMask } from "../ml/extractGarmentMask";
import { addWardrobeItem, getWardrobeItems, removeWardrobeItem } from "../state/wardrobeStore";
import { useSession } from "../hooks/useSession";

const CATEGORY_OPTIONS = [
  { id: "top", label: "Top" },
  { id: "bottom", label: "Bottom" },
  { id: "dress", label: "Dress" },
  { id: "outer", label: "Outerwear" },
];

// v1 default meshes by category (update as your real meshes evolve)
const ASSET_BASE =
  process.env.REACT_APP_ASSET_BASE_URL ||
  "https://pub-f0f38b8dd0224e2f89367893cf6bb0d7.r2.dev";

const DEFAULT_MESH_BY_CATEGORY = {
  top: `${ASSET_BASE}/garments/top_relaxed.glb`,
  bottom: `${ASSET_BASE}/garments/pant_relaxed.glb`,
  dress: `${ASSET_BASE}/garments/dress.glb`,
  outer: `${ASSET_BASE}/garments/outer_open.glb`,
};

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

export default function MyWardrobePanel({ open, onClose, onTryOn }) {
  const { isShopper } = useSession();
  const [mode, setMode] = useState("list"); // "list" | "add"
 const navigate = useNavigate();
 
  const [frontFile, setFrontFile] = useState(null);
  const [backFile, setBackFile] = useState(null);
  const [name, setName] = useState("");
  const [category, setCategory] = useState("top");

  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState("");

const [items, setItems] = useState([]);

async function refresh() {
  try {
    const next = await getWardrobeItems();
    setItems(Array.isArray(next) ? next : []);
  } catch (e) {
    console.warn("Failed to load wardrobe", e);
    setItems([]);
  }
}

 useEffect(() => {
  if (!open || !isShopper) return;
  refresh();
}, [open, isShopper]);

  const resetAddForm = () => {
    setFrontFile(null);
    setBackFile(null);
    setName("");
    setCategory("top");
    setSaving(false);
    setStatus("");
  };

  const handleSave = async () => {
    if (!frontFile || !backFile) return;
    if (!name.trim()) return;

    setSaving(true);
    setStatus("Preparing images...");

    try {
      const frontOriginalUrl = await readFileAsDataUrl(frontFile);
      const backOriginalUrl = await readFileAsDataUrl(backFile);

      setStatus("Extracting front...");
      const frontMask = await extractGarmentMask({ imageUrl: frontOriginalUrl, category });

      setStatus("Extracting back...");
      const backMask = await extractGarmentMask({ imageUrl: backOriginalUrl, category });

      const now = Date.now();

      const item = {
        id: `w_${now}`,
        name: name.trim(),
        category,
        brand: "My Wardrobe",
        meshUrl: DEFAULT_MESH_BY_CATEGORY[category],

        image: frontOriginalUrl,
        imageBack: backOriginalUrl,

        extractedUrl: frontMask?.dataUrl,
        extractedBackUrl: backMask?.dataUrl,

        source: "wardrobe",
        createdAt: now,
      };

await addWardrobeItem(item);
await refresh();
resetAddForm();
setMode("list");
    } catch (e) {
      console.error(e);
      setStatus(e?.message || "Failed to save. Try brighter lighting / simpler background.");
      setSaving(false);
    }
  };

  if (!open) return null;

  if (!isShopper) {
  return (
    <div className="side-panel left-panel">
      <div className="side-panel-header">
        <h2>My Wardrobe</h2>
        <button className="panel-close" onClick={onClose}>
          ✕
        </button>
      </div>

      <div className="side-panel-body">
  <p className="field-help" style={{ marginTop: 0 }}>
    Sign in to save pieces from your own wardrobe and try them on with the Veya catalog.
  </p>

  <button
    className="gold-btn"
    style={{ padding: "10px 12px", borderRadius: 12 }}
    onClick={() => {
      onClose?.();
      window.location.href = "/login";
    }}
  >
    Sign in
  </button>
</div>
    </div>
  );
}

  return (
    <div className="side-panel left-panel">
      <div className="side-panel-header">
        <h2>
          My Wardrobe{" "}
          <span style={{ fontSize: 12, opacity: 0.75, fontWeight: 500 }}>
            ({items?.length || 0})
          </span>
        </h2>

        <button className="panel-close" onClick={onClose}>
          ✕
        </button>
      </div>

      <div className="side-panel-body">
        <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
          <button
            className={mode === "list" ? "gold-btn" : "ghost-btn"}
            style={{ borderRadius: 12, padding: "10px 12px" }}
            onClick={() => setMode("list")}
          >
            Wardrobe
          </button>
          <button
            className={mode === "add" ? "gold-btn" : "ghost-btn"}
            style={{ borderRadius: 12, padding: "10px 12px" }}
            onClick={() => setMode("add")}
          >
            Add item
          </button>
        </div>

        {mode === "list" && (
          <>
            {items.length === 0 ? (
              <p className="field-help" style={{ marginTop: 0 }}>
                Your wardrobe is empty. Upload photos of garments from your own closet to try on with our catalog.
              </p>
            ) : (
              <div className="fitting-grid" style={{ gridTemplateColumns: "1fr" }}>
                {items.map((item) => (
                  <div
                    key={item.id}
                    className="FittingRoomPanel-item fitting-item"
                    onClick={() => onTryOn?.(item)}
                    style={{
                      textAlign: "left",
                      padding: 10,
                      display: "flex",
                      flexDirection: "column",
                      gap: 8,
                    }}
                  >
                    <button
                      className="remove-btn"
                      onClick={async (e) => {
  e.stopPropagation();
  await removeWardrobeItem(item.id);
  await refresh();
}}
                      aria-label="Remove item"
                      title="Remove"
                    >
                      ✕
                    </button>

                    <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                      <img
                        className="fitting-thumb"
                        src={item.image}
                        alt={item.name}
                        style={{
                          width: 64,
                          height: 64,
                          objectFit: "cover",
                          borderRadius: 12,
                          border: "1px solid rgba(43, 43, 43, 0.08)",
                          background: "rgba(251, 247, 242, 0.6)",
                        }}
                      />

                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          className="fitting-name"
                          style={{
                            marginTop: 0,
                            fontWeight: 600,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {item.name}
                        </div>
                        <div
                          className="fitting-category"
                          style={{ opacity: 0.75, textTransform: "capitalize" }}
                        >
                          {item.brand ? `${item.brand} • ` : ""}
                          {item.category}
                        </div>
                      </div>
                    </div>

                    <div style={{ display: "flex", gap: 8 }}>
                      <button
                        className="gold-btn"
                        style={{ flex: 1, padding: "10px 12px", borderRadius: 12 }}
                        onClick={(e) => {
                          e.stopPropagation();
                          onTryOn?.(item);
                        }}
                      >
                        Try on
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {mode === "add" && (
          <div className="panel-section" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div>
              <div style={{ fontWeight: 700, marginBottom: 6 }}>Photo guide (best results)</div>
              <ul style={{ margin: 0, paddingLeft: 18, opacity: 0.85, fontSize: 13, lineHeight: 1.4 }}>
                <li>Wear the garment and stand straight with arms relaxed at your sides.</li>
                <li>Take one front-facing photo, then one back-facing photo.</li>
                <li>Use natural light near a window. Face the light (don’t backlight).</li>
                <li>Keep the whole garment in frame. Use a plain background if possible.</li>
                <li>Avoid mirrors, harsh shadows, and wrinkled garments.</li>
              </ul>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <div style={{ fontWeight: 600, fontSize: 13 }}>Front photo</div>
                <input
  type="file"
  accept="image/*"
  capture="environment"
  disabled={saving}
  onChange={(e) => setFrontFile(e.target.files?.[0] || null)}
/>
{frontFile ? (
  <div style={{ fontSize: 12, opacity: 0.75 }}>
    Selected: {frontFile.name}
  </div>
) : null}

              </label>

              <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <div style={{ fontWeight: 600, fontSize: 13 }}>Back photo</div>
                <input
  type="file"
  accept="image/*"
  capture="environment"
  disabled={saving}
  onChange={(e) => setBackFile(e.target.files?.[0] || null)}
/>
{backFile ? (
  <div style={{ fontSize: 12, opacity: 0.75 }}>
    Selected: {backFile.name}
  </div>
) : null}
              </label>
            </div>

            <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <div style={{ fontWeight: 600, fontSize: 13 }}>Name</div>
              <input
                value={name}
                disabled={saving}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., White knit sweater"
                style={{ padding: "10px 12px", borderRadius: 12 }}
              />
            </label>

            <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <div style={{ fontWeight: 600, fontSize: 13 }}>Category</div>
              <select
                value={category}
                disabled={saving}
                onChange={(e) => setCategory(e.target.value)}
                style={{ padding: "10px 12px", borderRadius: 12 }}
              >
                {CATEGORY_OPTIONS.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.label}
                  </option>
                ))}
              </select>
            </label>

            {status ? <div style={{ fontSize: 12, opacity: 0.8 }}>{status}</div> : null}

            <div style={{ display: "flex", gap: 8 }}>
              <button
                className="ghost-btn"
                style={{ flex: 1, padding: "10px 12px", borderRadius: 12 }}
                disabled={saving}
                onClick={() => {
                  resetAddForm();
                  setMode("list");
                }}
              >
                Cancel
              </button>

              <button
                className="gold-btn"
                style={{ flex: 2, padding: "10px 12px", borderRadius: 12 }}
                disabled={saving || !frontFile || !backFile || !name.trim()}
                onClick={handleSave}
              >
                {saving ? "Saving..." : "Save to Wardrobe"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
